// Pure grouping algorithm for search results (by classification or geography).

import type {
  ClassificationGroupDepth,
  GroupPathSegment,
  ResultGroup,
  ResultGroupBy,
  SearchResult,
} from './types';

const NO_ADDRESS_LABEL = 'No address';
const NO_STREET_LABEL = 'No street';

function normaliseGroupKey(value: string): string {
  return value.trim().toLowerCase();
}

function groupSegment(value: string | null | undefined, fallback: string): GroupPathSegment {
  const label = value?.trim() || fallback;
  return { key: normaliseGroupKey(label), label };
}

function getRecordClassificationPath(result: SearchResult): GroupPathSegment[] {
  const classification = result.record_classification;
  return [
    groupSegment(classification?.level_1?.name, 'Unclassified function'),
    groupSegment(classification?.level_2?.name, 'Unclassified activity'),
    groupSegment(classification?.level_3?.name, 'Unclassified record type'),
  ];
}

function getFunctionClassificationPath(result: SearchResult): GroupPathSegment[] {
  const classification = result.function_classification;
  return [
    groupSegment(classification?.domain?.domain_name, 'Unclassified domain'),
    groupSegment(classification?.subdomain?.subdomain_name, 'Unclassified subdomain'),
    groupSegment(classification?.function?.function_name, 'Unclassified function'),
  ];
}

function getClassificationPath(
  result: SearchResult,
  groupBy: ResultGroupBy
): GroupPathSegment[] {
  if (groupBy === 'record') return getRecordClassificationPath(result);
  if (groupBy === 'function') return getFunctionClassificationPath(result);
  return [];
}

function getClassificationBreadcrumbSegment(
  result: SearchResult,
  groupBy: ResultGroupBy,
  depth: ClassificationGroupDepth
): GroupPathSegment | null {
  const path = getClassificationPath(result, groupBy).slice(0, depth);
  if (path.length === 0) return null;
  return {
    key: path.map((segment) => segment.key).join('::'),
    label: path.map((segment) => segment.label).join(' / '),
    title: path.map((segment) => segment.label).join(' / '),
  };
}

function getAddressSegments(result: SearchResult): GroupPathSegment[] {
  const segments: GroupPathSegment[] = [];
  for (const address of result.geographical_context?.addresses ?? []) {
    const label = address?.formatted_address?.trim();
    if (label) segments.push({ key: normaliseGroupKey(label), label, title: label });
  }
  return segments.length > 0 ? segments : [groupSegment(null, NO_ADDRESS_LABEL)];
}

function formatStreetLabel(part: {
  street_name?: string;
  street_type?: string;
  street_direction?: string;
  town_or_city?: string;
}): string {
  const street = [part.street_name?.trim(), part.street_type?.trim(), part.street_direction?.trim()]
    .filter(Boolean)
    .join(' ');
  if (!street) return '';
  const town = part.town_or_city?.trim();
  return town ? `${street}, ${town}` : street;
}

function getStreetSegments(result: SearchResult): GroupPathSegment[] {
  const context = result.geographical_context;
  const street = context?.street;
  const parts = [...(context?.addresses ?? []), ...(street ? [street] : [])];

  const segments: GroupPathSegment[] = [];
  for (const part of parts) {
    const label = formatStreetLabel(part);
    if (label) segments.push({ key: normaliseGroupKey(label), label, title: label });
  }
  return segments.length > 0 ? segments : [groupSegment(null, NO_STREET_LABEL)];
}

function getGroupSegments(
  result: SearchResult,
  groupBy: ResultGroupBy,
  depth: ClassificationGroupDepth
): GroupPathSegment[] {
  if (groupBy === 'address') return getAddressSegments(result);
  if (groupBy === 'street') return getStreetSegments(result);
  const segment = getClassificationBreadcrumbSegment(result, groupBy, depth);
  return segment ? [segment] : [];
}

function findOrCreateGroup(groups: ResultGroup[], segment: GroupPathSegment): ResultGroup {
  let group = groups.find((g) => g.key === segment.key);
  if (!group) {
    group = {
      key: segment.key,
      label: segment.label,
      title: segment.title,
      count: 0,
      children: [],
      results: [],
    };
    groups.push(group);
  }
  return group;
}

function addResultToGroup(
  groups: ResultGroup[],
  path: GroupPathSegment[],
  result: SearchResult
): void {
  let current = groups;
  let group: ResultGroup | undefined;

  for (const segment of path) {
    group = findOrCreateGroup(current, segment);
    group.count += 1;
    current = group.children;
  }

  group?.results.push(result);
}

/** Keeps the "no geography" bucket out of the middle of the relevance-ordered groups. */
function moveFallbackGroupLast(groups: ResultGroup[], fallbackLabel: string): ResultGroup[] {
  const fallbackKey = normaliseGroupKey(fallbackLabel);
  const index = groups.findIndex((group) => group.key === fallbackKey);
  if (index === -1) return groups;
  const reordered = [...groups];
  const [fallback] = reordered.splice(index, 1);
  reordered.push(fallback);
  return reordered;
}

export function groupResults(
  results: SearchResult[],
  groupBy: ResultGroupBy,
  classificationGroupDepth: ClassificationGroupDepth
): ResultGroup[] {
  const groups: ResultGroup[] = [];

  for (const result of results) {
    const segments = getGroupSegments(result, groupBy, classificationGroupDepth);
    const seenKeys = new Set<string>();
    for (const segment of segments) {
      if (seenKeys.has(segment.key)) continue;
      seenKeys.add(segment.key);
      addResultToGroup(groups, [segment], result);
    }
  }

  if (groupBy === 'address') return moveFallbackGroupLast(groups, NO_ADDRESS_LABEL);
  if (groupBy === 'street') return moveFallbackGroupLast(groups, NO_STREET_LABEL);
  return groups;
}
